import useAuthStore from '@/stores/auth'
import { Temporal } from 'temporal-polyfill'

const ignoreDatesBefore = Temporal.Instant.from('1980-01-01T00:00:00Z')

export const ONE_DAY_MS = 24 * 60 * 60 * 1000
export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000
export const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Represents the format of a date.
 */
export type DateFormat = 'dateOnly' | 'local' | 'rfc3339' | 'utc' | 'unix' | 'sql'

export interface DateFormatOption {
  value: DateFormat
  name: string
  alwaysTz?: string
}

export const dateFormatOptions: DateFormatOption[] = [
  { value: 'dateOnly', name: 'Date only (locale)' },
  { value: 'rfc3339', name: 'RFC3339' },
  { value: 'local', name: 'Date & time (locale)' },
  { value: 'utc', name: 'RFC 7231 (HTTP Date, UTC)', alwaysTz: 'UTC' },
  { value: 'unix', name: 'Unix timestamp', alwaysTz: 'UTC' },
  { value: 'sql', name: 'SQL timestamp' }
]

export function alwaysTimeZone(format: DateFormat): string | undefined {
  return dateFormatOptions.find((o) => o.value === format)?.alwaysTz
}

export function asDateFormat(value: string | undefined | null): DateFormat {
  return value && value in dateFormatConverters ? (value as DateFormat) : 'local'
}

export type DateInput = Date | string | number | Temporal.Instant

export type FractionalDigits = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9

const MAX_EPOCH_MS = 8.64e15

export interface FormatOptions {
  format?: DateFormat
  timeZone?: string
  showSeconds?: boolean
  showTimeZoneName?: boolean
  fullYear?: boolean
  fractionalDigits?: FractionalDigits
  parts?: Omit<
    Intl.DateTimeFormatOptions,
    'timeZone' | 'timeZoneName' | 'fractionalSecondDigits' | 'calendar' | 'numberingSystem'
  >
}

function fromEpochMs(ms: number): Temporal.Instant | undefined {
  if (Number.isNaN(ms) || Math.abs(ms) > MAX_EPOCH_MS) return undefined
  return Temporal.Instant.fromEpochMilliseconds(Math.trunc(ms))
}

function fromString(value: string): Temporal.Instant | undefined {
  try {
    return Temporal.Instant.from(value)
  } catch {
    return fromEpochMs(Date.parse(value))
  }
}

function toInstant(value: DateInput): Temporal.Instant | undefined {
  if (value instanceof Temporal.Instant) return value
  if (value instanceof Date) return fromEpochMs(value.getTime())
  if (typeof value === 'number') return fromEpochMs(value)
  return fromString(value)
}

function offsetDesignator(at: Temporal.ZonedDateTime): string {
  return at.offset === '+00:00' ? 'Z' : at.offset
}

function wallClock(
  at: Temporal.ZonedDateTime,
  separator: string,
  digits: FractionalDigits = 3
): string {
  return at
    .toPlainDateTime()
    .toString({ fractionalSecondDigits: digits })
    .replace('T', separator)
}

const dateFormatConverters: {
  [key in DateFormat]: (at: Temporal.ZonedDateTime, o: FormatOptions) => string
} = {
  dateOnly: (at, o) =>
    at.toLocaleString([], {
      year: o.fullYear ? 'numeric' : '2-digit',
      month: 'numeric',
      day: 'numeric',
      ...o.parts
    }),
  local: (at, o) =>
    at.toLocaleString([], {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: o.showTimeZoneName === false ? undefined : 'short',
      ...(o.showSeconds ? { second: '2-digit' } : {}),
      ...o.parts
    }),
  rfc3339: (at, o) => `${wallClock(at, 'T', o.fractionalDigits)}${offsetDesignator(at)}`,
  sql: (at, o) => wallClock(at, ' ', o.fractionalDigits),
  unix: (at) => String(at.epochNanoseconds / 1_000_000_000n),
  utc: (at) => new Date(at.epochMilliseconds).toUTCString()
}

function preferredTimeZone(): string | undefined {
  try {
    return useAuthStore().timeZone
  } catch {
    return undefined
  }
}

function preferredDateFormat(): DateFormat | undefined {
  try {
    return asDateFormat(useAuthStore().dateFormat)
  } catch {
    return undefined
  }
}

/**
 * Formats a given date according to `options.format` or the user's preferred date format.
 *
 * @param d - The date to format. A Date, an ISO string, an epoch millisecond count, a
 *   Temporal.Instant, or undefined.
 * @param options - `format` defaults to the user's preferred format from the auth store,
 *   `timeZone` to {@link currentTimeZone}, and `fractionalDigits` to milliseconds.
 * @returns A formatted date string, or undefined if the input is invalid or before 1980-01-01.
 */
export function formatInstant(at: Temporal.Instant, options: FormatOptions = {}): string {
  const format = options.format ?? preferredDateFormat() ?? 'local'
  return dateFormatConverters[format](zoned(at, options.timeZone ?? currentTimeZone()), options)
}

const formatDate = (
  d: DateInput | undefined | null,
  options: FormatOptions = {}
): string | undefined => {
  if (d === undefined || d === null || d === '') {
    return undefined
  }
  const at = toInstant(d)
  if (!at || Temporal.Instant.compare(at, ignoreDatesBefore) <= 0) {
    return undefined
  }
  return formatInstant(at, options)
}

export default formatDate

/**
 * Formats a date w/ seconds shown for any .to*String method. Lil helper.
 * @param d
 * @param options
 */
export const formatDateWithSecs = (d: DateInput | undefined, options: FormatOptions = {}) =>
  formatDate(d, { showSeconds: true, ...options })

/**
 * A bare day, e.g. "Aug 26", with the year only when it isn't the current one.
 * For dating a fact mid-sentence, where `formatDate`'s time is noise. Shared so
 * two callers rendering the same day can't disagree.
 */
export const formatDayLabel = (d: Date | string | number | undefined): string => {
  if (d === undefined || d === null) return ''
  const date = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(date.getTime())) return ''
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

export function browserTimeZone(): string {
  return Temporal.Now.timeZoneId()
}

const US_TIME_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
]

export interface TimeZoneOption {
  value: string
  name: string
}

export const LOCAL_TIME_ZONE = 'local'

const EPOCH = Temporal.Instant.fromEpochMilliseconds(0)

/**
 * Turns a stored preference into an IANA identifier that `Temporal` and `Intl` will
 * accept: the {@link LOCAL_TIME_ZONE} sentinel becomes the browser's zone, and an
 * unrecognized zone falls back to UTC.
 */
export function resolveTimeZone(zone: string): string {
  const resolved = zone === LOCAL_TIME_ZONE ? browserTimeZone() : zone
  try {
    EPOCH.toZonedDateTimeISO(resolved)
    return resolved
  } catch {
    return 'UTC'
  }
}

export function currentTimeZone(): string {
  return resolveTimeZone(preferredTimeZone() ?? LOCAL_TIME_ZONE)
}

export function isValidDate(d: Date): boolean {
  return !Number.isNaN(d.getTime())
}

function zoned(at: Temporal.Instant, zone: string): Temporal.ZonedDateTime {
  return at.toZonedDateTimeISO(resolveTimeZone(zone))
}

function zonedDateTime(d: Date, zone: string): Temporal.ZonedDateTime {
  return zoned(Temporal.Instant.fromEpochMilliseconds(d.getTime()), zone)
}

export function getZonedHours(d: Date, zone: string): number {
  return isValidDate(d) ? zonedDateTime(d, zone).hour : NaN
}

export function getZonedMinutes(d: Date, zone: string): number {
  return isValidDate(d) ? zonedDateTime(d, zone).minute : NaN
}

export function setZonedTime(d: Date, zone: string, hour: number, minute: number): Date {
  if (!isValidDate(d)) return new Date(NaN)
  const at = zonedDateTime(d, zone).with({ hour, minute, second: 0, millisecond: 0 })
  return new Date(at.epochMilliseconds)
}

/**
 * Calendar-day arithmetic in `zone`, so a step across a DST boundary still
 * lands on the same wall-clock time rather than drifting by an hour.
 */
export function addZonedDays(d: Date, days: number, zone: string = currentTimeZone()): Date {
  if (!isValidDate(d)) return new Date(NaN)
  return new Date(zonedDateTime(d, zone).add({ days }).epochMilliseconds)
}

/**
 * Calendar-month arithmetic in `zone`. Temporal constrains an overflowing day
 * (31 March minus one month is 28 February, not 3 March as `setMonth` gives).
 */
export function addZonedMonths(d: Date, months: number, zone: string = currentTimeZone()): Date {
  if (!isValidDate(d)) return new Date(NaN)
  return new Date(zonedDateTime(d, zone).add({ months }).epochMilliseconds)
}

/** Day of the week in `zone`, 0 = Sunday (matching `Date.prototype.getDay`). */
export function getZonedDayOfWeek(d: Date, zone: string = currentTimeZone()): number {
  if (!isValidDate(d)) return NaN
  return zonedDateTime(d, zone).dayOfWeek % 7
}

/** Calendar Y/M/D in `zone`, with `month` 1-indexed. */
export function getZonedDateParts(
  d: Date,
  zone: string = currentTimeZone()
): { year: number; month: number; day: number } {
  const at = zonedDateTime(d, zone)
  return { year: at.year, month: at.month, day: at.day }
}

/** The first instant of the given calendar day in `zone`. `month` is 1-indexed. */
export function startOfDayInZone(
  year: number,
  month: number,
  day: number,
  zone: string = currentTimeZone()
): Date {
  const plainDate = new Temporal.PlainDate(year, month, day)
  return new Date(plainDate.toZonedDateTime(resolveTimeZone(zone)).epochMilliseconds)
}

export function timeZoneLabel(zone: string): string {
  return zone === LOCAL_TIME_ZONE ? `Local (${browserTimeZone()})` : zone
}

export function timeZoneOptions(active: string): TimeZoneOption[] {
  const zones = Array.from(new Set([LOCAL_TIME_ZONE, 'UTC', ...US_TIME_ZONES, active]))
  return zones.map((value) => ({ value, name: timeZoneLabel(value) }))
}

/**
 * Converts a duration in milliseconds to a human-readable string representation.
 *
 * This function takes a duration in milliseconds and converts it to the largest
 * applicable unit (years, days, hours, minutes, or seconds). It returns a string
 * that represents the duration in a more readable format.
 *
 * @param milliseconds - The duration in milliseconds to be converted.
 * @param showTwoUnits - Show two units instead of one
 * @returns A string representing the duration in the largest applicable unit.
 *          The string includes the number and the unit name (pluralized if necessary).
 *          If the duration is less than one second, it returns 'less than a second'.
 */
export function millisecondsToStr(milliseconds: number, showTwoUnits: boolean = false): string {
  function numberEnding(n: number): '' | 's' {
    return n > 1 ? 's' : ''
  }

  let temp = Math.floor(milliseconds / 1000)
  const parts: string[] = []

  // Calculate all time units
  const years = Math.floor(temp / 31536000)
  temp %= 31536000
  const days = Math.floor(temp / 86400)
  temp %= 86400
  const hours = Math.floor(temp / 3600)
  temp %= 3600
  const minutes = Math.floor(temp / 60)
  const seconds = temp % 60

  // Build array of non-zero time parts
  if (years) parts.push(years + ' year' + numberEnding(years))
  if (days) parts.push(days + ' day' + numberEnding(days))
  if (hours) parts.push(hours + ' hour' + numberEnding(hours))
  if (minutes) parts.push(minutes + ' minute' + numberEnding(minutes))
  if (seconds) parts.push(seconds + ' second' + numberEnding(seconds))

  // Return based on showTwoUnits parameter
  if (parts.length === 0) {
    return 'less than a second'
  } else if (showTwoUnits && parts.length >= 2) {
    return parts[0] + ' ' + parts[1]
  } else {
    return parts[0]
  }
}

export const getDateDiffInMs = (date1: Date, date2: Date): number => {
  return Math.abs(date1.getTime() - date2.getTime());
}

/**
 * Takes a date and returns the first moment of its day.
 *
 * @param d Date
 */
export const getStartOfDay = (d: Date, zone: string = currentTimeZone()) =>
  isValidDate(d)
    ? new Date(zonedDateTime(d, zone).startOfDay().epochMilliseconds)
    : new Date(NaN)

/**
 * Takes a date and returns the last moment of its day.
 *
 * @param d Date
 */
export const getEndOfDay = (d: Date, zone: string = currentTimeZone()) =>
  isValidDate(d)
    ? new Date(zonedDateTime(d, zone).startOfDay().add({ days: 1 }).epochMilliseconds - 1)
    : new Date(NaN)

/**
 * Whether two dates fall on the same calendar day.
 */
export const isSameDay = (a: Date, b: Date, zone: string = currentTimeZone()): boolean =>
  isValidDate(a) &&
  isValidDate(b) &&
  zonedDateTime(a, zone).toPlainDate().equals(zonedDateTime(b, zone).toPlainDate())

/**
 * Generates tick values for a Unovis timeline, generating values based on length of range
 * @param start
 * @param end
 * @param zone Timezone the day/week/month boundaries are taken in
 */
export const getTickValuesForTimeline = (
  start: Date,
  end: Date,
  zone: string = currentTimeZone()
) => {
  const ticks: number[] = []
  const rangeMs = end.getTime() - start.getTime()
  const rangeDays = rangeMs / (24 * 60 * 60 * 1000)

  let intervalMs: number
  let intervalType: 'hour' | 'day' | 'week' | 'month'

  // Determine appropriate interval based on date range
  if (rangeDays <= 1) {
    // Less than 1 day: show every 4 hours
    intervalMs = 4 * 60 * 60 * 1000
    intervalType = 'hour'
  } else if (rangeDays <= 3) {
    // 1-3 days: show every 8 hours
    intervalMs = 8 * 60 * 60 * 1000
    intervalType = 'hour'
  } else if (rangeDays <= 7) {
    // 3-7 days: show daily
    intervalMs = 24 * 60 * 60 * 1000
    intervalType = 'day'
  } else if (rangeDays <= 14) {
    // 1-2 weeks: show every 2 days
    intervalMs = 2 * 24 * 60 * 60 * 1000
    intervalType = 'day'
  } else if (rangeDays <= 30) {
    // 2-4 weeks: show every 3 days
    intervalMs = 3 * 24 * 60 * 60 * 1000
    intervalType = 'day'
  } else if (rangeDays <= 90) {
    // 1-3 months: show weekly
    intervalMs = 7 * 24 * 60 * 60 * 1000
    intervalType = 'week'
  } else if (rangeDays <= 365) {
    // 3-12 months: show bi-weekly
    intervalMs = 14 * 24 * 60 * 60 * 1000
    intervalType = 'week'
  } else {
    // Over a year: show monthly
    intervalType = 'month'
    intervalMs = 30 * 24 * 60 * 60 * 1000 // Approximate
  }

  // Boundaries land in the user's zone, so a "day" tick sits on their midnight
  // rather than the browser's.
  let current = new Date(start)

  if (intervalType === 'month') {
    // Special handling for months to get clean month boundaries
    const { year, month } = getZonedDateParts(current, zone)
    current = startOfDayInZone(year, month, 1, zone)

    while (current <= end) {
      ticks.push(current.getTime())
      current = addZonedMonths(current, 1, zone)
    }
  } else {
    // For other intervals, start from a clean boundary
    if (intervalType === 'week') {
      // Start from beginning of week
      current = getStartOfDay(addZonedDays(current, -getZonedDayOfWeek(current, zone), zone), zone)
    } else if (intervalType === 'day') {
      // Start from beginning of day
      current = getStartOfDay(current, zone)
    } else {
      // For hours, round to nearest interval
      const intervalHours = intervalMs / (60 * 60 * 1000)
      const hours = Math.floor(getZonedHours(current, zone) / intervalHours) * intervalHours
      current = setZonedTime(getStartOfDay(current, zone), zone, hours, 0)
    }

    while (current <= end) {
      if (current >= start) {
        ticks.push(current.getTime())
      }
      current = new Date(current.getTime() + intervalMs)
    }
  }

  // Ensure we always have at least 2 ticks and at most 20
  if (ticks.length < 2) {
    ticks.push(start.getTime(), end.getTime())
  } else if (ticks.length > 20) {
    // Thin out ticks if we have too many
    const step = Math.ceil(ticks.length / 15)
    const thinnedTicks = []
    for (let i = 0; i < ticks.length; i += step) {
      thinnedTicks.push(ticks[i])
    }
    return thinnedTicks
  }

  return ticks
}
